import React, { useState } from 'react';
import { Col, Form, Row, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

/**
 * Componente para mostrar y editar el Source del lead con dropdown
 */
const LeadSourceSection = ({ 
    detallesLead, 
    ActualizarFuente
}) => {
    const [editarSource, setEditarSource] = useState(false);
    const [sourceSeleccionado, setSourceSeleccionado] = useState(detallesLead?.idSource || 0);

    if (!detallesLead) {
        return null;
    }

    const getSourceName = (idSource) => {
        const sources = {
            1: "WhatsApp",
            2: "Messenger",
            3: "Facebook",
            4: "Tawk",
            5: "Correo",
            6: "AkasiaBot",
            7: "Distribuidor"
        };
        return sources[idSource] || "---";
    };

    const getSourceIcon = (idSource) => {
        const icons = {
            1: "/img/leads/WhatsApp48.png",
            2: "/img/leads/Messenger48.png",
            3: "/img/leads/Facebook48.png",
            4: "/img/leads/Tawk48.png",
            5: "/img/leads/Correo48.png",
            6: "/img/leads/AkasiaBot48.png",
            7: "/img/leads/Distribuidor48.png"
        };
        return icons[idSource];
    };

    const handleGuardarSource = () => {
        if (sourceSeleccionado > 0) {
            ActualizarFuente(getSourceName(sourceSeleccionado));
            setEditarSource(false);
        }
    };

    const handleCancelar = () => {
        setSourceSeleccionado(detallesLead.idSource);
        setEditarSource(false);
    };

    // Vista cuando no hay source asignado (idSource === 0)
    if (detallesLead.idSource === 0) {
        const sourcesButtons = [
            { id: 1, name: "WhatsApp", color: "success" },
            { id: 2, name: "Messenger", color: "primary" },
            { id: 3, name: "Facebook", color: "info" },
            { id: 4, name: "Tawk", color: "warning" },
            { id: 5, name: "Correo", color: "secondary" },
            { id: 6, name: "AkasiaBot", color: "dark" },
            { id: 7, name: "Distribuidor", color: "primary" }
        ];

        return (
            <Row className="mb-3">
                <Col className="col-12">
                    <div className="alert alert-warning d-flex align-items-center">
                        <FontAwesomeIcon icon="exclamation-triangle" className="me-2" size="lg" />
                        <div className="flex-grow-1">
                            <strong>Selecciona la fuente de este lead</strong>
                            <p className="mb-0 fs--1">Es necesario asignar de dónde proviene este contacto.</p>
                            <strong>Otro vendedor podrá asignárselo.</strong>
                        </div>
                    </div>
                </Col>
                <Col className="col-12">
                    <Row className="g-2">
                        {sourcesButtons.map((source) => (
                            <Col key={source.id} xs={6} sm={4} md={3} className="d-grid">
                                <Button
                                    variant={`outline-${source.color}`}
                                    className="d-flex flex-column align-items-center justify-content-center py-3"
                                    onClick={() => ActualizarFuente(source.name)}
                                >
                                    <img 
                                        src={getSourceIcon(source.id)} 
                                        width={40} 
                                        alt={source.name}
                                        className="mb-2"
                                    />
                                    <span className="fw-semi-bold fs--1">{source.name}</span>
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>
        );
    }

    const sourceName = getSourceName(detallesLead.idSource);

    // Vista de edición
    if (editarSource) {
        return (
            <Col className="col-12 mb-3">
                <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon="share-alt" className="text-muted" />
                    <span className="fw-semi-bold text-muted fs--1">Fuente:</span>
                    <Form.Select
                        size="sm"
                        aria-label="Seleccionar Source"
                        value={sourceSeleccionado}
                        onChange={(event) => setSourceSeleccionado(parseInt(event.target.value, 10))}
                        style={{ width: 'auto', minWidth: '150px' }}
                    >
                        <option value="0">Seleccionar...</option>
                        <option value="1">WhatsApp</option>
                        <option value="2">Messenger</option>
                        <option value="3">Facebook</option>
                        <option value="4">Tawk</option>
                        <option value="5">Correo</option>
                        <option value="6">AkasiaBot</option>
                        <option value="7">Distribuidor</option>
                    </Form.Select>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleGuardarSource}
                        disabled={sourceSeleccionado === 0}
                    >
                        <img src="/img/check25.png" width="20" alt="Confirmar" />
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleCancelar}
                    >
                        ✕
                    </Button>
                </div>
            </Col>
        );
    }

    // Vista normal (solo lectura con opción de editar)
    return (
        <Col className="col-12 mb-2">
            <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon="share-alt" className="text-muted" />
                <span className="fw-semi-bold text-muted fs--1">Fuente:</span>
                <img 
                    src={getSourceIcon(detallesLead.idSource)} 
                    width={28} 
                    alt={sourceName}
                />
                <span className="fs--1 fw-semi-bold">{sourceName}</span>
                <OverlayTrigger 
                    placement="top"
                    overlay={<Tooltip id="tooltip-source">Editar fuente del lead</Tooltip>}
                >
                    <button
                        className="btn btn-link p-0 ms-1"
                        onClick={() => setEditarSource(true)}
                        style={{ textDecoration: 'none' }}
                    >
                        <FontAwesomeIcon 
                            icon="edit" 
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                            size="sm"
                        />
                    </button>
                </OverlayTrigger>
            </div>
        </Col>
    );
};

LeadSourceSection.propTypes = {
    detallesLead: PropTypes.shape({
        idLead: PropTypes.number,
        idSource: PropTypes.number,
        vNombreLead: PropTypes.string
    }).isRequired,
    ActualizarFuente: PropTypes.func.isRequired
};

export default LeadSourceSection;
